<template>
    <b-container
            fluid
            class="custom-address p-0"
    >
        <b-form-group
                v-if="!isCustomAddressSelected"
                :label="searchLabel"
                label-for="company-address-input"
                label-class="company-form-label"
                class="company-form-group search-address"
        >
            <address-type-ahead
                    class="company-form input-group prepend-icon-input-group"
                    :placeholder-fallback="placeholder"
                    input-class="border-gray-500 border-left-0 pl-0"
                    v-model="addressResource"
                    :no-padding="true"
                    :is-zip-code-required="true"
                    :reset-searched-value.sync="resetSearchedAddress"
                    :preselected-address="preselectedAddress"
                    :preselected-address-name="preselectedAddressName"
                    :should-search-preselected-address="shouldSearchPreselectedAddress"
                    :ref="getAddressRef"
                    :token="token"
                    :invitation-key="invitationKey"
                    :is-opt-in-pending-invitation="isOptInPendingInvitation"
                    :has-focusable-state="hasFocusableState"
                    @input="handleSearchAddressSelection"
            >
                <template v-slot:before>
                    <slot name="before" />
                    <b-input-group-prepend v-if="!hasCustomBeforeSlot">
                        <b-button
                                class="border-gray-500"
                                disabled
                        >
                            <i class="far fa-map-marker-alt" />
                        </b-button>
                    </b-input-group-prepend>
                </template>
                <template
                        v-slot:extension
                        v-if="canAddCustomAddress"
                >
                    <b-dropdown-group
                            id="not-scrollable-group"
                            class="not-scrollable-group"
                    >
                        <b-dropdown-divider />
                        <b-dropdown-item
                                class="not-clickable-area"
                                @click="handleNonClickableOption"
                        >
                            <div class="d-flex justify-content-between">
                                <span>Don't see your address?</span>
                                <span
                                        class="clickable-area text-primary"
                                        :class="swapTextClass"
                                        @click="addCustomAddress"
                                >
                                Add It Manually
                            </span>
                            </div>
                        </b-dropdown-item>
                    </b-dropdown-group>
                </template>
            </address-type-ahead>
            <div
                    v-if="showEnterManuallyButton"
                    class="clickable-area text-address address-action"
                    :class="swapTextClass"
                    @click="addCustomAddress"
            >
                <slot name="manual-button-content">
                    <span>Enter Manually</span>
                </slot>
            </div>
            <form-errors
                    v-if="errors.locations"
                    :errors="errors"
                    field="locations"
            />
        </b-form-group>
        <div v-show="isCustomAddressSelected">
            <b-form-group
                    :label="streetLabel"
                    label-for="company-street-input"
                    label-class="company-form-label"
                    class="company-form-group"
            >
                <b-input-group>
                    <b-form-input
                            v-model="firstStreet"
                            id="company-street-input"
                            class="mb-2"
                            @input="setStreetAddress"
                            trim
                    />
                </b-input-group>
                <b-input-group>
                    <b-form-input
                            v-model="secondStreet"
                            id="company-second-street-input"
                            @input="setStreetAddress"
                            trim
                    />
                </b-input-group>
            </b-form-group>
            <b-form-row>
                <b-col :class="small ? 'col-12' : 'col-sm-8'">
                    <b-form-group
                            :label="cityStateLabel"
                            label-for="company-city-input"
                            label-class="company-form-label"
                            class="company-form-group city-state-group"
                            :class="{'no-bottom-margin': !small}"
                    >
                        <b-form-input
                                :placeholder="cityStatePlaceholder"
                                :value="cityAndState"
                                :disabled="disabledIndex !== undefined"
                                @input="handleInputCityAndState"
                        />
                        <vue-custom-scrollbar
                                v-if="dropdownShow"
                                :settings="scrollSettings"
                                id="city-state-dropdown"
                                :class="{'city-state-menu': cities.length > 0}"
                                class="scroll"
                        >
                            <li
                                    class="dropdown-item"
                                    v-for="(city, index) in cities"
                                    :key="`cityIndex-${index}`"
                                    @click="handleSelectForCityAndState(city, index)"
                            >
                    <span>
                        {{ `${city.name}, ${city.state}` }}
                        <span class="float-right text-gray-600">
                            <template v-if="disabledIndex === index">
                                <b-spinner
                                        variant="primary"
                                        small
                                        label="Spinning"
                                />
                            </template>
                            <template v-else>
                                City, State
                            </template>
                        </span>
                    </span>
                            </li>
                        </vue-custom-scrollbar>
                    </b-form-group>
                </b-col>
                <b-col :class="small ? 'col-12' : 'col-sm-4'">
                    <b-form-group
                            :label="zipLabel"
                            label-for="company-zip-input"
                            label-class="company-form-label"
                            class="company-form-group zip"
                    >
                        <b-form-input
                                v-model="zipCode"
                                id="company-zip-input"
                                pattern="\d{5}"
                                maxlength="5"
                                @input="setZipCode"
                                trim
                        />
                        <form-errors
                                v-if="errors.locations"
                                :errors="errors"
                                field="locations"
                        />
                    </b-form-group>
                </b-col>
            </b-form-row>
            <b-form-row>
                <span
                        class="clickable-area text-address text-left pl-1 address-action"
                        :class="swapTextClass"
                        @click="changeToSearchAddressView"
                >
                    Search Address
                </span>
            </b-form-row>
            <form-errors
                    v-if="errors['locations.notFound']"
                    :errors="errors"
                    dot-field="locations.notFound"
            />
        </div>
    </b-container>
</template>

<script>
import FormErrors from '@milestoneslabs/milestones-ui-kit/src/components/FormErrors/FormErrors.vue';
import './CustomAddressTypeAhead.scss';
import AddressTypeAhead from "../../components/AddressTypeAhead/AddressTypeAhead.vue";
import VueCustomScrollbar from 'vue-custom-scrollbar';
import customAddressTypeAheadMixin from "./customAddressTypeAheadMixin";

export default {
    name: "CustomAddressTypeAhead",
    mixins: [
        customAddressTypeAheadMixin,
    ],
    components: {
        FormErrors,
        AddressTypeAhead,
        VueCustomScrollbar,
    },
};
</script>
